/**
 * Shared game utilities used by both client and server.
 */

import type { Card, Player } from "./types";

// ============================================================================
// Shuffle Utilities
// ============================================================================

/**
 * Fisher-Yates shuffle algorithm - produces unbiased random permutation.
 * Creates a new array, does not mutate the original.
 */
export function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ============================================================================
// Team Validation
// ============================================================================

/** Shuffle players array randomly using Fisher-Yates */
export function shufflePlayers(players: Player[]): Player[] {
  return shuffle(players);
}

/** Check if teams are properly configured to start game */
export function teamsAreReady(players: Player[]): boolean {
  // Only count connected players assigned to teams
  // connected !== false treats undefined as connected (backwards compatible)
  const assignedPlayers = players.filter((p) => p.team && p.role && p.connected !== false);
  if (assignedPlayers.length < 4) return false;

  const redTeam = assignedPlayers.filter((player) => player.team === "red");
  const blueTeam = assignedPlayers.filter((player) => player.team === "blue");

  // Each team needs exactly 1 clue giver (game mechanic requirement)
  const redClueGivers = redTeam.filter((player) => player.role === "clueGiver").length;
  const blueClueGivers = blueTeam.filter((player) => player.role === "clueGiver").length;

  return redClueGivers === 1 && blueClueGivers === 1;
}

/** Calculate required votes to reveal a card */
export function getRequiredVotes(guesserCount: number): number {
  return guesserCount >= 4 ? 2 : 1;
}

// ============================================================================
// Reveal / Game Over Helpers
// ============================================================================

/**
 * True only for cards revealed by an actual seeker action during gameplay.
 * Batch game-over reveals set `revealed: true` without `revealedBy`.
 */
export function wasCardRevealedDuringPlay(card: Pick<Card, "revealedBy">): boolean {
  return typeof card.revealedBy === "string" && card.revealedBy.length > 0;
}

/** Count team cards that were genuinely revealed during gameplay (not auto-reveal). */
export function countTeamCardsRevealedDuringPlay(
  board: Card[],
  team: "red" | "blue"
): number {
  return board.filter((card) => card.team === team && wasCardRevealedDuringPlay(card)).length;
}

/** Detect whether the trap was actually hit during gameplay. */
export function wasTrapHitDuringPlay(board: Card[]): boolean {
  return board.some(
    (card) => card.team === "trap" && wasCardRevealedDuringPlay(card)
  );
}

// ============================================================================
// Clue Validation
// ============================================================================

/**
 * Validate a clue word against the board.
 * Returns an error message string if invalid, or null if valid.
 * 
 * Checks:
 * - Exact match with board words
 * - Prefix/suffix relationships (blocks "farm"/"farmer", allows "war"/"dwarf")
 * - Plural variants are caught by the prefix/suffix check (dog/dogs, bench/benches)
 */
export function getClueValidationError(word: string, boardWords: string[]): string | null {
  const normalized = word.toUpperCase();
  const boardWordsUpper = boardWords.map((w) => w.toUpperCase());
  const boardWordsSet = new Set(boardWordsUpper);

  // Exact match check
  if (boardWordsSet.has(normalized)) {
    return `"${word}" is a word on the board`;
  }

  // Check if clue is a prefix/suffix of any board word or vice versa.
  // This blocks meaningful derivations like "farm"/"farmer", but allows
  // coincidental substrings like "war" in "dwarf".
  // Note: This also handles plural variants (dog/dogs, bench/benches) since
  // adding/removing S or ES creates a prefix/suffix relationship.
  for (const boardWord of boardWordsUpper) {
    // Clue is prefix or suffix of board word
    if (boardWord.startsWith(normalized) || boardWord.endsWith(normalized)) {
      return `"${word}" is too similar to "${boardWord}"`;
    }
    // Board word is prefix or suffix of clue
    if (normalized.startsWith(boardWord) || normalized.endsWith(boardWord)) {
      return `"${word}" is too similar to "${boardWord}"`;
    }
  }

  return null;
}

/**
 * Validate a clue word against the board.
 * Returns true if valid, false if invalid.
 */
export function isValidClue(word: string, boardWords: string[]): boolean {
  return getClueValidationError(word, boardWords) === null;
}
